/**
 * [web/navigation] Public entry points and safe workbench login destinations.
 * Owns local return paths; it never grants access or accepts external redirect destinations.
 */
const publicPaths = new Set(['/', '/download', '/guide', '/releases']);
const RETURN_KEY = 'uplab_web_return';

function unsafePathCharacters(value: string): boolean {
  return Array.from(value).some((character) => {
    const code = character.charCodeAt(0);
    return character === '\\' || code <= 32 || code === 127;
  });
}

export function isPublicSitePath(pathname: string): boolean {
  return publicPaths.has(pathname.replace(/\/+$/, '') || '/');
}

export function workspaceReturnTo(value: string | null | undefined): string {
  if (!value?.startsWith('/') || value.startsWith('//') || unsafePathCharacters(value))
    return '/app';
  const url = new URL(value, 'https://uplab.local');
  let decoded: string;
  try {
    decoded = decodeURIComponent(url.pathname);
  } catch {
    return '/app';
  }
  if (unsafePathCharacters(decoded) || decoded.startsWith('//')) return '/app';
  const pathname = new URL(decoded, 'https://uplab.local').pathname;
  if (
    url.origin !== 'https://uplab.local' ||
    isPublicSitePath(pathname) ||
    /^\/(login|sso|api)(\/|$)/u.test(pathname)
  )
    return '/app';
  return `${url.pathname}${url.search}${url.hash}`;
}

export function loginHref(returnTo: string): string {
  return `/login?${new URLSearchParams({ returnTo: workspaceReturnTo(returnTo) })}`;
}

/** Preserve a local Web destination across the IdP round trip; desktop handoff remains independent. */
export function ssoReturnTo(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
  search: string,
  now = Date.now(),
): string {
  const query = new URLSearchParams(search);
  const supplied = query.get('returnTo');
  if (supplied !== null) {
    const path = workspaceReturnTo(supplied);
    try {
      storage.setItem(RETURN_KEY, JSON.stringify({ path, expires: now + 10 * 60_000 }));
    } catch {
      /* Navigation persistence may be unavailable; sign-in can still finish at the workbench. */
    }
    return path;
  }
  try {
    const entry: unknown = JSON.parse(storage.getItem(RETURN_KEY) ?? 'null');
    if (
      entry &&
      typeof entry === 'object' &&
      'expires' in entry &&
      typeof entry.expires === 'number' &&
      entry.expires > now &&
      'path' in entry &&
      typeof entry.path === 'string'
    )
      return workspaceReturnTo(entry.path);
  } catch {
    /* Invalid or expired navigation state falls back to the workbench. */
  }
  try {
    storage.removeItem(RETURN_KEY);
  } catch {
    /* Optional navigation storage can be disabled by the browser. */
  }
  return '/app';
}

export function clearSsoReturn(storage: Pick<Storage, 'removeItem'>): void {
  try {
    storage.removeItem(RETURN_KEY);
  } catch {
    /* Clearing optional navigation state cannot block sign-in. */
  }
}
